import React, { useMemo, useState, useEffect } from 'react';
import { Form, Row, Col, Input, Select, DatePicker, TreeSelect, Radio, Alert, Checkbox, Space, Button, message } from 'antd';
import { PlusOutlined, ExportOutlined } from '@ant-design/icons';
import Tabs from './common/LegacyTabs';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { trpc } from '../lib/api';
import ParentEquipmentSelect from './ParentEquipmentSelect';
import { QuickAddCategoryModal } from './QuickAddCategoryModal';
import { withDevModalFilePath, NumericInput, RichTextEditor } from './common';
import { useCompanyStore } from '../stores/companyStore';
import { useFormAutoBackup } from '../hooks/useFormAutoBackup';

const { Modal } = withDevModalFilePath('client/src/components/EquipmentFormModal.tsx');
import {
  ENV_PRESETS,
  MAT_PRESETS,
  EQUIPMENT_TYPE_PRESETS,
  getEnvPresetByKey,
  getMatPresetByKey,
  getEquipmentTypePreset,
  getEnvPresetByValue,
  getMatPresetByValue,
} from '../constants/deterioration-presets';
import {
  EUL_TYPE_OPTIONS,
  DEFAULT_EUL_TYPE,
  EUL_TYPE_HELP_TEXT,
  calculateEulB632,
  isEulType,
  type EulType,
} from '../constants/eul-types';
type MaybeDateInput = Date | Dayjs | string | null | undefined;
type MaybeNumberInput = number | string | null | undefined;

interface LocationTreeNode {
  id: string;
  name: string;
  children?: LocationTreeNode[] | null;
}

interface TreeNodeOption {
  title: string;
  value: string;
  key: string;
  children?: TreeNodeOption[];
}

interface EquipmentFormValues extends Record<string, unknown> {
  id?: string;
  displayId?: string;
  parentId?: string | null;
  locationIds?: string[] | string | null;
  name?: string;
  code?: string | null;
  category?: string | null;
  manufacturerSupplierId?: string | null; // メーカー業者ID
  salesSupplierId?: string | null; // 販売業者ID
  maintenanceSupplierId?: string | null; // メンテナンス業者ID
  model?: string | null;
  serialNumber?: string | null;
  userId?: string | null;
  dpEnvPreset?: string | null;
  dpMatPreset?: string | null;
  dpEquipmentType?: string | null;
  dpEulType?: EulType | null;
  dpEulInput?: MaybeNumberInput;
  dpEul?: MaybeNumberInput;
  dpKMaint?: MaybeNumberInput;
  dpKConst?: MaybeNumberInput;
  dpBeta?: MaybeNumberInput;
  dpKEnv?: MaybeNumberInput;
  dpKMat?: MaybeNumberInput;
  manufacturerLifespan?: MaybeNumberInput; // メーカー耐用年数（年）
  price?: MaybeNumberInput;
  importanceLevel?: MaybeNumberInput; // 重要度: 1:低, 2:中, 3:高, 4:最重要
  commissionedDate?: MaybeDateInput; // 稼働開始日（統合カラム）
  installationDate?: MaybeDateInput;
  notes?: string | null;
}

interface FormValidationError {
  errorFields?: Array<{ name?: (string | number)[] }>;
}

const READ_ONLY_CALCULATED_FIELDS = [
  'currentDeteriorationRate',
  'predictedDeteriorationRate',
  'healthIndex',
  'alertThreshold',
  'depreciationRate',
  'currentDepreciationRate',
  'predictedDepreciationRate',
  'maintenanceScore',
  'estimatedLifespanYears',
  'maintenanceIntervalDays',
] as const;

const TAB_KEY_BASIC = '1';
const TAB_KEY_HEALTH = '2';

const FIELD_TAB_MAP: Record<string, string> = {
  name: TAB_KEY_BASIC,
  category: TAB_KEY_BASIC,
  locationIds: TAB_KEY_BASIC,
  parentId: TAB_KEY_BASIC,
  manufacturerSupplierId: TAB_KEY_BASIC,
  salesSupplierId: TAB_KEY_BASIC,
  maintenanceSupplierId: TAB_KEY_BASIC,
  model: TAB_KEY_BASIC,
  serialNumber: TAB_KEY_BASIC,
  fixedAssetNumber: TAB_KEY_BASIC,
  commissionedDate: TAB_KEY_BASIC,
  installationDate: TAB_KEY_BASIC,
  price: TAB_KEY_BASIC,
  notes: TAB_KEY_BASIC,

  manufacturerLifespan: TAB_KEY_HEALTH,
  importanceLevel: TAB_KEY_HEALTH,
  dpEquipmentType: TAB_KEY_HEALTH,
  dpEulType: TAB_KEY_HEALTH,
  dpEulInput: TAB_KEY_HEALTH,
  dpEul: TAB_KEY_HEALTH,
  dpBeta: TAB_KEY_HEALTH,
  dpKEnv: TAB_KEY_HEALTH,
  dpKMat: TAB_KEY_HEALTH,
  dpKMaint: TAB_KEY_HEALTH,
  dpKConst: TAB_KEY_HEALTH,
  dpEnvPreset: TAB_KEY_HEALTH,
  dpMatPreset: TAB_KEY_HEALTH,
};

const isFormValidationError = (error: unknown): error is FormValidationError => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  return Array.isArray((error as FormValidationError).errorFields);
};

const getTabKeyFromNamePath = (namePath?: (string | number)[]): string | null => {
  if (!Array.isArray(namePath) || namePath.length === 0) {
    return null;
  }

  const fieldName = String(namePath[0]);
  return FIELD_TAB_MAP[fieldName] ?? null;
};

const collectErrorTabKeys = (errorFields: Array<{ name?: (string | number)[] }>): string[] => {
  const keys = new Set<string>();

  errorFields.forEach((field) => {
    const tabKey = getTabKeyFromNamePath(field.name);
    if (tabKey) {
      keys.add(tabKey);
    }
  });

  return Array.from(keys);
};

const normalizeTabKey = (tabKey: string): string => {
  if (tabKey.startsWith('.$')) {
    return tabKey.slice(2);
  }
  return tabKey;
};

type EquipmentSubmitValues = Omit<
  EquipmentFormValues,
  | 'dpEulInput'
  | 'dpEul'
  | 'dpKMaint'
  | 'dpKConst'
  | 'dpBeta'
  | 'dpKEnv'
  | 'dpKMat'
  | 'manufacturerLifespan'
  | 'price'
  | 'importanceLevel'
> & {
  dpEulInput?: number | null;
  dpEul?: number | null;
  dpKMaint?: number | null;
  dpKConst?: number | null;
  dpBeta?: number | null;
  dpKEnv?: number | null;
  dpKMat?: number | null;
  manufacturerLifespan?: number | null;
  price?: number | null;
  importanceLevel?: number | null;
};

const DATE_KEYS = [
  'commissionedDate',
  'installationDate',
] as const;

const FORM_DEFAULTS: EquipmentFormValues = {
  dpEulType: DEFAULT_EUL_TYPE,
  dpKMaint: 1.0,
  dpKConst: 1.0,
  locationIds: null,
};

interface EquipmentFormModalProps {
  open: boolean;
  title?: string;
  confirmLoading?: boolean;
  initialValues?: EquipmentFormValues | null;
  currentId?: string | null;
  onCancel: () => void;
  onSubmit: (payload: { values: EquipmentSubmitValues; locationIds: string[] }) => void | Promise<void>;
}

const EquipmentFormModal: React.FC<EquipmentFormModalProps> = ({
  open,
  title = '設備編集',
  confirmLoading,
  initialValues,
  currentId = null,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm<EquipmentFormValues>();
  const [parentType, setParentType] = useState<'location' | 'equipment'>('location');
  const [selectedEquipmentType, setSelectedEquipmentType] = useState<string | null>(null);
  const [suggestedEnv, setSuggestedEnv] = useState<string | null>(null);
  const [suggestedMat, setSuggestedMat] = useState<string | null>(null);
  const [advancedMode, setAdvancedMode] = useState<boolean>(false);
  const [manualEnv, setManualEnv] = useState<boolean>(false);
  const [manualMat, setManualMat] = useState<boolean>(false);
  const [showQuickAddCategory, setShowQuickAddCategory] = useState<boolean>(false);
  const [currentTabKey, setCurrentTabKey] = useState<string>(TAB_KEY_BASIC);
  const [errorTabKeys, setErrorTabKeys] = useState<string[]>([]);

  // 編集モードかどうか（IDがある場合は編集）
  const isEditMode = !!(initialValues?.id || currentId);

  // フォーム自動バックアップ（新規作成のみ有効、編集時は無効）
  const { hasBackup, showRestoreConfirm, clearBackup } = useFormAutoBackup({
    formType: 'equipment',
    entityId: undefined, // 新規作成のみなのでIDは不要
    form,
    enabled: open && !isEditMode,
  });

  // 健全度管理方式を取得
  const { healthManagementMode, setHealthManagementMode } = useCompanyStore();
  const { data: currentHealthMode } = trpc.company.getHealthManagementMode.useQuery();

  useEffect(() => {
    if (currentHealthMode) {
      setHealthManagementMode(currentHealthMode);
    }
  }, [currentHealthMode, setHealthManagementMode]);

  const isManufacturerLifespanMode = (currentHealthMode || healthManagementMode) === 'manufacturer_lifespan';

  // エリアツリー
  const { data: locationsTree } = trpc.location.getTree.useQuery();

  // カテゴリ一覧取得
  const { data: categories = [] } = trpc.equipmentCategory.list.useQuery({
    includeInactive: false,
    sortBy: 'display_order',
    sortOrder: 'asc',
  });

  // 業者一覧取得
  const { data: suppliersData } = trpc.suppliers.list.useQuery({
    page: 1,
    limit: 100, // 最大100件まで取得
  });
  const suppliers = suppliersData?.items || [];

  // カテゴリ追加成功時のハンドラー
  const handleCategoryAdded = (categoryId: string, _categoryLabel: string) => {
    // 新しく作成されたカテゴリを選択
    form.setFieldsValue({ category: categoryId });
    setShowQuickAddCategory(false);
  };

  const convertToTreeSelectData = (locations: LocationTreeNode[] | null | undefined): TreeNodeOption[] => {
    if (!locations?.length) return [];
    return locations.map((location) => ({
      title: location.name,
      value: location.id,
      key: location.id,
      children: location.children ? convertToTreeSelectData(location.children) : undefined,
    }));
  };
  const treeSelectData = convertToTreeSelectData((locationsTree as LocationTreeNode[] | undefined) ?? []);

  const normalizeDates = (values: EquipmentFormValues): EquipmentFormValues => {
    // タイムゾーン問題を回避するため、Date オブジェクトではなく YYYY-MM-DD 形式の文字列を返す
    // JSON.stringify 時の UTC 変換による1日ずれを防止
    const toDateString = (v: MaybeDateInput): string | null => {
      if (!v) return null;
      if (dayjs.isDayjs(v)) return v.format('YYYY-MM-DD');
      if (v instanceof Date) return dayjs(v).format('YYYY-MM-DD');
      const d = dayjs(v);
      return d.isValid() ? d.format('YYYY-MM-DD') : null;
    };
    return {
      ...values,
      commissionedDate: toDateString(values.commissionedDate),
    };
  };

  const processedInitialValues = useMemo<EquipmentFormValues>(() => {
    const base: EquipmentFormValues = { ...FORM_DEFAULTS };
    if (!initialValues) {
      return base;
    }
    const cloned: EquipmentFormValues = { ...base, ...initialValues };

    const rawCategory = (initialValues as { category?: unknown } | null)?.category;
    if (rawCategory && typeof rawCategory === 'object') {
      const categoryObj = rawCategory as { id?: string | null; categoryKey?: string | null };
      cloned.category = categoryObj.id ?? categoryObj.categoryKey ?? null;
    }
    if (!cloned.category) {
      const categoryId = (initialValues as { categoryId?: string | null } | null)?.categoryId;
      if (categoryId) {
        cloned.category = categoryId;
      }
    }
    if (cloned.category != null && typeof cloned.category !== 'string') {
      cloned.category = String(cloned.category);
    }
    for (const key of DATE_KEYS) {
      const v = cloned[key];
      if (!v) continue;
      if (dayjs.isDayjs(v)) continue;
      const d = dayjs(v);
      if (d.isValid()) cloned[key] = d;
    }
    if (Array.isArray(cloned.locationIds)) {
      cloned.locationIds = cloned.locationIds[0] ?? null;
    }
    if (cloned.locationIds === undefined) {
      cloned.locationIds = null;
    }

    // 既存の数値からプリセットキーを逆引き
    if (cloned.dpKEnv !== undefined && cloned.dpKEnv !== null) {
      const envPreset = getEnvPresetByValue(Number(cloned.dpKEnv));
      if (envPreset) {
        cloned.dpEnvPreset = envPreset.key;
      }
    }
    if (cloned.dpKMat !== undefined && cloned.dpKMat !== null) {
      const matPreset = getMatPresetByValue(Number(cloned.dpKMat));
      if (matPreset) {
        cloned.dpMatPreset = matPreset.key;
      }
    }
    if (cloned.dpKMaint === undefined || cloned.dpKMaint === null) {
      cloned.dpKMaint = FORM_DEFAULTS.dpKMaint;
    }
    if (cloned.dpKConst === undefined || cloned.dpKConst === null) {
      cloned.dpKConst = FORM_DEFAULTS.dpKConst;
    }

    if (!isEulType(cloned.dpEulType)) {
      cloned.dpEulType = DEFAULT_EUL_TYPE;
    }
    if (cloned.dpEulInput !== undefined && cloned.dpEulInput !== null) {
      const numeric = Number(cloned.dpEulInput);
      if (!Number.isNaN(numeric)) {
        cloned.dpEulInput = numeric;
      }
    }

    // 設備タイプがある場合は推奨設定を表示
    if (cloned.dpEquipmentType) {
      const preset = getEquipmentTypePreset(cloned.dpEquipmentType);
      if (preset) {
        setTimeout(() => {
          setSelectedEquipmentType(cloned.dpEquipmentType);
          setSuggestedEnv(preset.suggestedEnv);
          setSuggestedMat(preset.suggestedMat);
        }, 0);
      }
    }

    return cloned;
  }, [initialValues]);

  // 初期値から親階層タイプを判定
  useEffect(() => {
    if (initialValues) {
      if (initialValues.parentId) {
        setParentType('equipment');
        return;
      }
      const locationValue = initialValues.locationIds;
      const hasLocation =
        (Array.isArray(locationValue) && locationValue.length > 0) ||
        (typeof locationValue === 'string' && locationValue);
      if (hasLocation) {
        setParentType('location');
      }
    }
  }, [initialValues]);

  // モーダルが開かれたときにフォームの値を明示的に設定
  useEffect(() => {
    if (open && processedInitialValues) {
      form.setFieldsValue(processedInitialValues);
    }
  }, [open, processedInitialValues, form]);

  // モーダルが開かれた時にバックアップの確認を表示
  useEffect(() => {
    if (open && hasBackup) {
      // 初期値設定後に確認ダイアログを表示
      const timer = setTimeout(() => {
        showRestoreConfirm();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, hasBackup, showRestoreConfirm]);

  const normalizeNumbers = (values: EquipmentFormValues): EquipmentSubmitValues => {
    const toNumber = (v: MaybeNumberInput): number | null | undefined => {
      if (v === undefined) return undefined;
      if (v === null || v === '') return null;
      const num = Number(v);
      return isNaN(num) ? null : num;
    };

    const {
      dpEulInput: _dpEulInput,
      dpEul: _dpEul,
      dpKMaint: _dpKMaint,
      dpKConst: _dpKConst,
      dpBeta: _dpBeta,
      dpKEnv: _dpKEnv,
      dpKMat: _dpKMat,
      manufacturerLifespan: _manufacturerLifespan,
      price: _price,
      importanceLevel: _importanceLevel,
      ...baseValues
    } = values;
    void _dpEulInput;
    void _dpEul;
    void _dpKMaint;
    void _dpKConst;
    void _dpBeta;
    void _dpKEnv;
    void _dpKMat;
    void _manufacturerLifespan;
    void _price;
    void _importanceLevel;

    const dpEulInputNumber = toNumber(values.dpEulInput);
    const result: EquipmentSubmitValues = { ...baseValues };

    // 計算値カラムは編集フォームの入力対象外のため送信しない
    for (const key of READ_ONLY_CALCULATED_FIELDS) {
      delete (result as Record<string, unknown>)[key];
    }

    const dpEul = toNumber(values.dpEul);
    if (dpEul !== undefined) result.dpEul = dpEul;
    if (dpEulInputNumber !== undefined) result.dpEulInput = dpEulInputNumber;

    const dpKMaint = toNumber(values.dpKMaint);
    if (dpKMaint !== undefined) result.dpKMaint = dpKMaint;

    const dpKConst = toNumber(values.dpKConst);
    if (dpKConst !== undefined) result.dpKConst = dpKConst;

    const dpBeta = toNumber(values.dpBeta);
    if (dpBeta !== undefined) result.dpBeta = dpBeta;

    const dpKEnv = toNumber(values.dpKEnv);
    if (dpKEnv !== undefined) result.dpKEnv = dpKEnv;

    const dpKMat = toNumber(values.dpKMat);
    if (dpKMat !== undefined) result.dpKMat = dpKMat;

    const manufacturerLifespan = toNumber(values.manufacturerLifespan);
    if (manufacturerLifespan !== undefined) result.manufacturerLifespan = manufacturerLifespan;

    const price = toNumber(values.price);
    if (price !== undefined) result.price = price;

    const importanceLevel = toNumber(values.importanceLevel);
    if (importanceLevel !== undefined) result.importanceLevel = importanceLevel;

    const normalizedType: EulType = isEulType(result.dpEulType) ? result.dpEulType : DEFAULT_EUL_TYPE;
    result.dpEulType = normalizedType;

    const beta = typeof result.dpBeta === 'number' ? result.dpBeta : undefined;
    if (typeof dpEulInputNumber === 'number') {
      const computed = calculateEulB632(dpEulInputNumber, normalizedType, beta);
      if (computed !== null) {
        result.dpEul = computed;
      }
    }

    return result;
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    setCurrentTabKey(TAB_KEY_BASIC);
    setErrorTabKeys([]);
  }, [open]);

  const renderTabLabel = (label: string, key: string): React.ReactNode => (
    <span>
      {label}
      {errorTabKeys.includes(key) && (
        <span style={{ color: '#ff4d4f', marginLeft: 8, fontSize: 12, fontWeight: 600 }}>
          要確認
        </span>
      )}
    </span>
  );

  return (
    <Modal
      title={title}
      open={open}
      onOk={async () => {
        try {
          await form.validateFields();
          const values = form.getFieldsValue(true) as EquipmentFormValues;
          const { locationIds, ...rest } = values;
          const normalized = normalizeDates(rest as EquipmentFormValues);
          const numericNormalized = normalizeNumbers(normalized);

          // 親階層タイプに応じて、不要なフィールドを明示的にクリア
          if (parentType === 'equipment') {
            // 親設備モード: locationIds を空配列にして locationId をクリア
            await onSubmit({ values: numericNormalized, locationIds: [] });
          } else {
            // 配置エリアモード: parentId を null にクリア
            numericNormalized.parentId = null;
            const locationId = Array.isArray(locationIds) ? locationIds[0] : locationIds ?? null;
            await onSubmit({ values: numericNormalized, locationIds: locationId ? [locationId] : [] });
          }
          // 保存成功時にバックアップをクリア
          setErrorTabKeys([]);
          clearBackup();
        } catch (error) {
          // フォーム入力エラーは AntD の標準表示に任せ、未捕捉 Promise エラーを防ぐ
          if (isFormValidationError(error)) {
            const nextErrorTabKeys = collectErrorTabKeys(error.errorFields ?? []);
            setErrorTabKeys(nextErrorTabKeys);

            if (nextErrorTabKeys.length > 0) {
              const hiddenErrorTabs = nextErrorTabKeys.filter((tabKey) => tabKey !== currentTabKey);
              if (hiddenErrorTabs.length > 0) {
                const hiddenTabNames = hiddenErrorTabs.map((tabKey) =>
                  tabKey === TAB_KEY_HEALTH ? '健全度/現況' : '基本情報'
                );
                message.warning(`他のタブにも入力エラーがあります（${hiddenTabNames.join(' / ')}）`);
              }
            }

            const firstErrorField = error.errorFields?.[0];
            if (Array.isArray(firstErrorField?.name) && firstErrorField.name.length > 0) {
              form.scrollToField(firstErrorField.name);
            }
          }
        }
      }}
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
      width={800}
      confirmLoading={confirmLoading}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={processedInitialValues}
        onFieldsChange={(_, allFields) => {
          const fieldsWithErrors = allFields.filter((field) => Array.isArray(field.errors) && field.errors.length > 0);
          setErrorTabKeys(collectErrorTabKeys(fieldsWithErrors.map((field) => ({ name: field.name }))));
        }}
      >
        <Tabs defaultActiveKey={TAB_KEY_BASIC} onChange={(key) => setCurrentTabKey(normalizeTabKey(String(key)))}>
          <Tabs.TabPane tab={renderTabLabel('基本情報', TAB_KEY_BASIC)} key={TAB_KEY_BASIC}>
            <Row gutter={16}>
              <Col span={16}>
                <Form.Item
                  name="name"
                  label="設備名"
                  rules={[{ required: true, message: '設備名を入力してください' }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="category"
                  label={
                    <Space>
                      <span>カテゴリ</span>
                      <Button
                        type="link"
                        size="small"
                        icon={<ExportOutlined />}
                        onClick={() => window.open('/app/categories', '_blank')}
                        style={{ padding: 0 }}
                      >
                        管理画面
                      </Button>
                    </Space>
                  }
                  rules={[{ required: true, message: 'カテゴリを選択してください' }]}
                >
                  <Select
                    placeholder="カテゴリを選択"
                    loading={!categories}
                    popupRender={(menu) => (
                      <>
                        {menu}
                        <Button
                          type="text"
                          icon={<PlusOutlined />}
                          onClick={() => setShowQuickAddCategory(true)}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            borderTop: '1px solid #f0f0f0',
                            borderRadius: 0,
                          }}
                        >
                          新規カテゴリを追加
                        </Button>
                      </>
                    )}
                  >
                    {categories.map((cat) => (
                      <Select.Option key={cat.id} value={cat.id}>
                        {cat.categoryLabel}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label="配置先"
                  tooltip="設備の配置先を選択します。エリアまたは親設備のどちらか一方のみ設定できます。"
                >
                  <Space.Compact style={{ width: '100%' }}>
                    <Radio.Group
                      value={parentType}
                      onChange={(e) => {
                        setParentType(e.target.value);
                        if (e.target.value === 'location') {
                          form.setFieldsValue({ parentId: undefined });
                        } else {
                          form.setFieldsValue({ locationIds: undefined });
                        }
                      }}
                      style={{ flexShrink: 0 }}
                    >
                      <Radio value="location">エリア</Radio>
                      <Radio value="equipment">親設備</Radio>
                    </Radio.Group>
                    {parentType === 'location' ? (
                      <Form.Item name="locationIds" noStyle>
                        <TreeSelect
                          treeData={treeSelectData}
                          placeholder="エリアを選択"
                          allowClear
                          showSearch
                          style={{ flex: 1, minWidth: 200 }}
                          treeDefaultExpandAll
                          onChange={() => {
                            form.setFieldsValue({ parentId: undefined });
                          }}
                        />
                      </Form.Item>
                    ) : (
                      <Form.Item name="parentId" noStyle>
                        <ParentEquipmentSelect
                          currentId={currentId || null}
                          inline
                          style={{ flex: 1, minWidth: 200 }}
                          onChange={() => {
                            form.setFieldsValue({ locationIds: undefined });
                          }}
                        />
                      </Form.Item>
                    )}
                  </Space.Compact>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="manufacturerSupplierId" label="メーカー業者">
                  <Select
                    placeholder="メーカー業者を選択"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={suppliers.map((supplier) => ({
                      label: supplier.name,
                      value: supplier.id,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="salesSupplierId" label="販売業者">
                  <Select
                    placeholder="販売業者を選択"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={suppliers.map((supplier) => ({
                      label: supplier.name,
                      value: supplier.id,
                    }))}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="maintenanceSupplierId" label="メンテナンス業者">
                  <Select
                    placeholder="メンテナンス業者を選択"
                    allowClear
                    showSearch
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={suppliers.map((supplier) => ({
                      label: supplier.name,
                      value: supplier.id,
                    }))}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item name="model" label="型番">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="serialNumber" label="シリアル番号">
                  <Input />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="fixedAssetNumber" label="固定資産番号">
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="commissionedDate" label="稼働開始日">
                  <DatePicker style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="price" label="価格">
                  <NumericInput style={{ width: '100%' }} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              name="notes"
              label="詳細"
              getValueFromEvent={(markdown: string) => markdown}
              getValueProps={(value) => ({ value: value || '' })}
            >
              <RichTextEditor
                placeholder="詳細を入力してください..."
                minHeight={170}
                showPreviewToggle={false}
              />
            </Form.Item>
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={renderTabLabel('健全度/現況', TAB_KEY_HEALTH)}
            key={TAB_KEY_HEALTH}
            forceRender
          >
            {/* 健全度管理方式の表示 */}
            <Alert
              message={
                isManufacturerLifespanMode
                  ? '📋 耐用年数モード'
                  : '⚙️ 健全度モード'
              }
              description={
                isManufacturerLifespanMode
                  ? 'メーカー指定の耐用年数を基準にシンプルな計算を行います。会社設定から変更できます。'
                  : '環境係数やメンテナンス係数などを設定して、詳細な健全度予測を行います。会社設定から変更できます。'
              }
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            {/* メーカー耐用年数Ver */}
            {isManufacturerLifespanMode && (
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="manufacturerLifespan"
                    label="メーカー耐用年数（年）"
                    tooltip="メーカーが推奨する設備の耐用年数を入力してください"
                    rules={[
                      { required: true, message: 'メーカー耐用年数を入力してください' },
                      { type: 'number', min: 1, max: 100, message: '1~100年の範囲で入力してください' },
                    ]}
                  >
                    <NumericInput
                      style={{ width: '100%' }}
                      placeholder="例: 10"
                      addonAfter="年"
                      min={1}
                      max={100}
                      step={0.5}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="importanceLevel"
                    label="重要度"
                    tooltip="設備の重要度を設定してください"
                  >
                    <Select placeholder="重要度を選択">
                      <Select.Option value={1}>低</Select.Option>
                      <Select.Option value={2}>中</Select.Option>
                      <Select.Option value={3}>高</Select.Option>
                      <Select.Option value={4}>最重要</Select.Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
            )}

            {/* 複雑Ver（ワイブル分布） */}
            {!isManufacturerLifespanMode && (
              <>
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={24}>
                    <Checkbox
                      checked={advancedMode}
                      onChange={(e) => setAdvancedMode(e.target.checked)}
                    >
                      詳細設定モード（専門家向け）
                    </Checkbox>
                  </Col>
                </Row>

                {!advancedMode && selectedEquipmentType && (suggestedEnv || suggestedMat) && (
                  <Alert
                    message="💡 設備タイプを選ぶと、推奨設定が自動で適用されます"
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                )}

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="dpEquipmentType" label="設備タイプ">
                      <Select
                        placeholder="設備タイプを選択"
                        showSearch
                        style={{ width: '100%' }}
                        onChange={(value) => {
                          setSelectedEquipmentType(value);
                          const preset = getEquipmentTypePreset(value);
                          if (preset) {
                            form.setFieldsValue({ dpBeta: preset.beta });
                            setSuggestedEnv(preset.suggestedEnv);
                            setSuggestedMat(preset.suggestedMat);

                            const envPreset = getEnvPresetByKey(preset.suggestedEnv);
                            const matPreset = getMatPresetByKey(preset.suggestedMat);
                            if (envPreset) {
                              form.setFieldsValue({
                                dpEnvPreset: preset.suggestedEnv,
                                dpKEnv: envPreset.value
                              });
                            }
                            if (matPreset) {
                              form.setFieldsValue({
                                dpMatPreset: preset.suggestedMat,
                                dpKMat: matPreset.value
                              });
                            }
                          }
                        }}
                      >
                        {EQUIPMENT_TYPE_PRESETS.map((preset) => (
                          <Select.Option key={preset.label} value={preset.label}>
                            {preset.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name="dpEulType"
                      label="期待耐用年数の指標タイプ"
                      tooltip="メーカーが提供している信頼性指標を選択してください"
                      rules={[{ required: true, message: '指標タイプを選択してください' }]}
                    >
                      <Select
                        placeholder="指標タイプを選択"
                        options={EUL_TYPE_OPTIONS.map(({ value, label }) => ({ value, label }))}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      noStyle
                      shouldUpdate={(prevValues, currentValues) =>
                        prevValues.dpEulType !== currentValues.dpEulType
                      }
                    >
                      {({ getFieldValue }) => {
                        const eulTypeValue = getFieldValue('dpEulType');
                        const tooltip = isEulType(eulTypeValue) ? EUL_TYPE_HELP_TEXT[eulTypeValue] : undefined;
                        return (
                          <Form.Item
                            name="dpEulInput"
                            label="メーカー提供の耐用年数"
                            tooltip={tooltip}
                            rules={[
                              {
                                type: 'number',
                                min: 0,
                                message: '0以上の数値を入力してください',
                              },
                            ]}
                          >
                            <NumericInput
                              style={{ width: '100%' }}
                              placeholder="年数を入力"
                              addonAfter="年"
                              min={0}
                              step={0.1}
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.dpEulInput !== currentValues.dpEulInput ||
                    prevValues.dpEulType !== currentValues.dpEulType ||
                    prevValues.dpBeta !== currentValues.dpBeta
                  }
                >
                  {({ getFieldValue }) => {
                    const rawInput = getFieldValue('dpEulInput');
                    const rawType = getFieldValue('dpEulType');
                    const rawBeta = getFieldValue('dpBeta');
                    const numericInput = typeof rawInput === 'number' ? rawInput : Number(rawInput);
                    const validType: EulType = isEulType(rawType) ? rawType : DEFAULT_EUL_TYPE;
                    const numericBeta = typeof rawBeta === 'number' ? rawBeta : Number(rawBeta);
                    const betaValue = Number.isFinite(numericBeta) && numericBeta > 0 ? numericBeta : undefined;

                    let description = '入力値を入力するとB63.2相当値が表示されます';
                    if (Number.isFinite(numericInput)) {
                      if (validType === 'UNKNOWN') {
                        description = `入力値 (${numericInput} 年) がそのまま使用されます`;
                      } else {
                        const computed = calculateEulB632(numericInput, validType, betaValue);
                        if (computed !== null) {
                          const betaDisplay = betaValue ?? 2;
                          description = `約 ${computed.toFixed(2)} 年 (β=${betaDisplay}) として保存されます`;
                        }
                      }
                    }

                    return (
                      <Alert
                        message="計算後のB63.2相当値"
                        description={
                          <div>
                            <div>{description}</div>
                            <div style={{ marginTop: 4, fontSize: 12, color: '#666' }}>
                              ※ 設置環境・材質係数は健全度推移に反映され、この換算値は変わりません。
                            </div>
                          </div>
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                    );
                  }}
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label={advancedMode ? '設置環境 (K_env)' : '設置環境'}>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        {!manualEnv && (
                          <Form.Item name="dpEnvPreset" noStyle>
                            <Select
                              placeholder="環境を選択"
                              style={{ width: '100%' }}
                              optionLabelProp="label"
                              disabled={manualEnv}
                              onChange={(value) => {
                                const preset = getEnvPresetByKey(value);
                                if (preset) {
                                  form.setFieldsValue({ dpKEnv: preset.value });
                                }
                              }}
                            >
                              {ENV_PRESETS.map((preset) => (
                                <Select.Option
                                  key={preset.key}
                                  value={preset.key}
                                  label={preset.label}
                                  style={{ height: 'auto', padding: '8px 12px' }}
                                >
                                  <div>{preset.label}</div>
                                  <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                                    {preset.description}
                                  </div>
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        )}
                        {advancedMode && (
                          <Checkbox
                            checked={manualEnv}
                            onChange={(e) => {
                              setManualEnv(e.target.checked);
                              if (!e.target.checked) {
                                const presetKey = form.getFieldValue('dpEnvPreset');
                                if (presetKey) {
                                  const preset = getEnvPresetByKey(presetKey);
                                  if (preset) {
                                    form.setFieldsValue({ dpKEnv: preset.value });
                                  }
                                }
                              }
                            }}
                          >
                            マニュアル入力
                          </Checkbox>
                        )}
                        {manualEnv && (
                          <Form.Item name="dpKEnv" noStyle>
                            <NumericInput
                              style={{ width: '100%' }}
                              min={0.1}
                              max={2.0}
                              step={0.01}
                              placeholder="1.0"
                            />
                          </Form.Item>
                        )}
                        {!advancedMode && suggestedEnv && (
                          <div style={{ fontSize: '12px', color: '#1890ff' }}>
                            💡 推奨: {getEnvPresetByKey(suggestedEnv)?.label}
                          </div>
                        )}
                      </Space>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label={advancedMode ? '材質 (K_mat)' : '材質'}>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        {!manualMat && (
                          <Form.Item name="dpMatPreset" noStyle>
                            <Select
                              placeholder="材質を選択"
                              style={{ width: '100%' }}
                              optionLabelProp="label"
                              disabled={manualMat}
                              onChange={(value) => {
                                const preset = getMatPresetByKey(value);
                                if (preset) {
                                  form.setFieldsValue({ dpKMat: preset.value });
                                }
                              }}
                            >
                              {MAT_PRESETS.map((preset) => (
                                <Select.Option
                                  key={preset.key}
                                  value={preset.key}
                                  label={preset.label}
                                  style={{ height: 'auto', padding: '8px 12px' }}
                                >
                                  <div>{preset.label}</div>
                                  <div style={{ fontSize: '12px', color: '#888', marginTop: '2px' }}>
                                    {preset.description}
                                  </div>
                                </Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        )}
                        {advancedMode && (
                          <Checkbox
                            checked={manualMat}
                            onChange={(e) => {
                              setManualMat(e.target.checked);
                              if (!e.target.checked) {
                                const presetKey = form.getFieldValue('dpMatPreset');
                                if (presetKey) {
                                  const preset = getMatPresetByKey(presetKey);
                                  if (preset) {
                                    form.setFieldsValue({ dpKMat: preset.value });
                                  }
                                }
                              }
                            }}
                          >
                            マニュアル入力
                          </Checkbox>
                        )}
                        {manualMat && (
                          <Form.Item name="dpKMat" noStyle>
                            <NumericInput
                              style={{ width: '100%' }}
                              min={0.1}
                              max={2.0}
                              step={0.01}
                              placeholder="1.0"
                            />
                          </Form.Item>
                        )}
                        {!advancedMode && suggestedMat && (
                          <div style={{ fontSize: '12px', color: '#1890ff' }}>
                            💡 推奨: {getMatPresetByKey(suggestedMat)?.label}
                          </div>
                        )}
                      </Space>
                    </Form.Item>
                  </Col>
                </Row>


                {/* 隠しフィールド: 数値は裏で自動設定 */}
                <Form.Item name="dpEul" hidden>
                  <NumericInput />
                </Form.Item>
                <Form.Item name="dpBeta" hidden>
                  <NumericInput />
                </Form.Item>
                <Form.Item name="dpKEnv" hidden>
                  <NumericInput />
                </Form.Item>
                <Form.Item name="dpKMat" hidden>
                  <NumericInput />
                </Form.Item>
                <Form.Item name="dpKMaint" hidden>
                  <NumericInput />
                </Form.Item>
                <Form.Item name="dpKConst" hidden>
                  <NumericInput />
                </Form.Item>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="importanceLevel" label="重要度">
                      <Select>
                        <Select.Option value={1}>低</Select.Option>
                        <Select.Option value={2}>中</Select.Option>
                        <Select.Option value={3}>高</Select.Option>
                        <Select.Option value={4}>要注意</Select.Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
          </Tabs.TabPane>
        </Tabs>
      </Form>

      {/* カテゴリ追加モーダル */}
      <QuickAddCategoryModal
        open={showQuickAddCategory}
        onCancel={() => setShowQuickAddCategory(false)}
        onSuccess={handleCategoryAdded}
      />
    </Modal>
  );
};

export default EquipmentFormModal;

